export type ReaderBookmark = {
  bookId: string;
  chapterSlug: string;
  flatLeftIndex: number;
  paragraphIndex: number;
  savedAt: string;
};

/** Router `location.state` shape for jumping to a paragraph / spread after navigation. */
export type ReaderResumePayload = { paragraphIndex?: number; flatLeftIndex?: number };

function storageKey(bookId: string): string {
  return `aistorycast-reader-place-${bookId}`;
}

/** Accept only plain objects with finite numeric indices (ignore arrays, strings, etc.). */
export function coerceReaderResumePayload(raw: unknown): ReaderResumePayload | null {
  if (raw == null) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const out: ReaderResumePayload = {};
  if (typeof o.paragraphIndex === "number" && Number.isFinite(o.paragraphIndex)) {
    out.paragraphIndex = Math.max(0, Math.floor(o.paragraphIndex));
  }
  if (typeof o.flatLeftIndex === "number" && Number.isFinite(o.flatLeftIndex)) {
    out.flatLeftIndex = Math.max(0, Math.floor(o.flatLeftIndex));
  }
  if (out.paragraphIndex === undefined && out.flatLeftIndex === undefined) return null;
  return out;
}

export function loadReaderBookmark(bookId: string): ReaderBookmark | null {
  if (typeof window === "undefined") return null;
  if (typeof bookId !== "string" || bookId.trim().length === 0) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(bookId));
    if (!raw?.trim()) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const o = parsed as Record<string, unknown>;
    if (typeof o.bookId !== "string" || o.bookId !== bookId) return null;
    if (typeof o.chapterSlug !== "string" || o.chapterSlug.length === 0) return null;
    if (typeof o.flatLeftIndex !== "number" || !Number.isFinite(o.flatLeftIndex)) return null;
    if (typeof o.paragraphIndex !== "number" || !Number.isFinite(o.paragraphIndex)) return null;
    if (typeof o.savedAt !== "string" || o.savedAt.length === 0) return null;
    return {
      bookId: o.bookId,
      chapterSlug: o.chapterSlug,
      flatLeftIndex: Math.max(0, Math.floor(o.flatLeftIndex)),
      paragraphIndex: Math.max(0, Math.floor(o.paragraphIndex)),
      savedAt: o.savedAt,
    };
  } catch {
    return null;
  }
}

export function saveReaderBookmark(bookmark: ReaderBookmark): void {
  if (typeof window === "undefined") return;
  try {
    if (
      typeof bookmark.bookId !== "string" ||
      bookmark.bookId.trim().length === 0 ||
      typeof bookmark.chapterSlug !== "string" ||
      typeof bookmark.savedAt !== "string"
    ) {
      return;
    }
    window.localStorage.setItem(storageKey(bookmark.bookId), JSON.stringify(bookmark));
  } catch {
    /* quota or private mode */
  }
}

export function clearReaderBookmark(bookId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(bookId));
  } catch {
    /* ignore */
  }
}
